// Batch save (PUT /items/:tenant): the items, the deletes and the modifiche of
// one operation in a single request, so a single commit and push on the
// backend. The pure parts live here, out of worker.js (which imports the
// socket, the memory DB and the App), so they can be tested.

import { draftKey } from "../draft/store";

// The modifiche hold something to execute: modifiche[azione][tipo][id].
// doRemote adds the branch to the object it sends, which is not a modifica.
export function hasModifiche(modifiche) {
  if (!modifiche || typeof modifiche !== "object") return false;

  return Object.entries(modifiche).some(
    ([azione, tipi]) =>
      azione !== "branch" &&
      tipi &&
      typeof tipi === "object" &&
      Object.values(tipi).some(
        (ids) => ids && typeof ids === "object" && Object.keys(ids).length > 0
      )
  );
}

function withoutBranch(modifiche) {
  const { branch, ...rest } = modifiche;
  return rest;
}

// The batch has something to save besides the item it is built around.
export function hasBatchContent(batch) {
  if (!batch) return false;

  return (
    batch.items?.length > 0 ||
    batch.deletes?.length > 0 ||
    hasModifiche(batch.modifiche)
  );
}

// Items and deletes come in the shape of saveDataItem / deleteDataItem
// ({ accessor, Id, prevId, isNew, data, skipdeps } / { accessor, Id,
// reference }). baseVersion is sent only when the caller gives it (phase 1).
export function buildBatchPayload({
  items = [],
  deletes = [],
  modifiche
} = {}) {
  const payload = {
    items: items.map(
      ({
        accessor,
        Id,
        prevId,
        isNew,
        skipdeps,
        skipDeps,
        baseVersion,
        data
      }) => ({
        type: accessor,
        Id,
        prevId,
        isNew,
        skipDeps: Boolean(skipDeps ?? skipdeps),
        ...(baseVersion !== undefined && { baseVersion }),
        data
      })
    ),
    deletes: deletes.map(({ accessor, Id, id, reference, baseVersion }) => ({
      type: accessor,
      id: Id ?? id,
      reference,
      ...(baseVersion !== undefined && { baseVersion })
    }))
  };

  if (hasModifiche(modifiche)) payload.modifiche = withoutBranch(modifiche);

  return payload;
}

// The rows a batch sends, as draft keys ("accessor:id"): the draft store
// tells our own saved rows from the ones edited while the request ran.
export function batchKeys({ items = [], deletes = [] } = {}) {
  return [
    ...items.map(({ accessor, Id }) => draftKey(accessor, Id)),
    ...deletes.map(({ accessor, Id, id }) => draftKey(accessor, Id ?? id))
  ];
}

// The table of a type of the modifiche (the server's names: modifiche.ts)
const modificheTable = (tipo) =>
  tipo === "endpoint" ? "endpoints" : tipo === "page" ? "blocks" : tipo;

// The rows the modifiche delete (modifiche.delete[tipo][id]), as draft
// keys: our own write, as the rows a batch sends (the `sent` of the
// refresh), so that their delete is not a colleague's (a conflict of a
// draft, the hold of an open form). Not the rows the server builds from the
// modifiche (create, update) nor the refs.belongs it updates: not rows we
// sent, a draft of them stays a conflict. The server checks no version of
// modifiche: a delete that must not remove a colleague's save goes in the
// deletes of a batch with its baseVersion (the cascade of a property
// deleted from its list: propertiesList/cascade.js).
export function modificheDeletes(modifiche) {
  const deletes = modifiche?.delete;
  if (!deletes || typeof deletes !== "object") return [];

  return Object.entries(deletes).flatMap(([tipo, ids]) =>
    ids && typeof ids === "object"
      ? Object.keys(ids).map((id) => draftKey(modificheTable(tipo), id))
      : []
  );
}

// Backend without PUT /items: the calls of before, one after the other, in
// the order the operations made them (the items, then the deletes, then the
// modifiche, e.g. the property and then the modifiche of a new variant). The
// first failure stops the sequence and is rejected as the call rejected it.
export async function runBatchFallback(
  { items = [], deletes = [], modifiche, branch } = {},
  { saveDataItem, deleteDataItem, saveModifiche }
) {
  const results = [];

  for (const item of items)
    results.push(await saveDataItem({ ...item, branch }));

  for (const del of deletes)
    results.push(
      await deleteDataItem({ ...del, Id: del.Id ?? del.id, branch })
    );

  if (hasModifiche(modifiche))
    results.push(await saveModifiche({ ...withoutBranch(modifiche), branch }));

  return results;
}

export const conflictError = (data) => ({
  type: "conflict",
  conflicts: data?.conflicts ?? []
});

export const batchError = (error) => ({ type: "error", error });

// The refusals of the API that list their issues
const INVALID_CONFIG = new Set(["INVALID_LOADER_CONFIG", "EXPERIMENT_INVALID"]);
const MAX_ISSUES_SHOWN = 3;

// The API refuses a Loader whose Merge is not valid, or a save that breaks the
// rules of a started experiment (HTTP 400, body { status: "KO", error:
// "INVALID_LOADER_CONFIG" | "EXPERIMENT_INVALID", loaderId | experimentId,
// issues: [{ code, field, message }] }): the messages of its issues, the
// first three and how many more, instead of the bare error code. Undefined
// for any other answer, which keeps the error it always had.
export function invalidConfigText(data) {
  if (!INVALID_CONFIG.has(data?.error) || !Array.isArray(data.issues))
    return undefined;

  const messages = data.issues
    .map((issue) => issue?.message)
    .filter((message) => typeof message === "string" && message.trim());
  if (messages.length === 0) return undefined;

  const more = messages.length - MAX_ISSUES_SHOWN;
  const shown = messages.slice(0, MAX_ISSUES_SHOWN).join(" ");
  return more > 0 ? `${shown} +${more}` : shown;
}

// Rejections of saveBatch built here (not the ones of the fallback calls,
// which keep the shape they always had).
export const isBatchError = (error) =>
  error !== null &&
  typeof error === "object" &&
  !(error instanceof Error) &&
  (error.type === "conflict" || error.type === "error");

// request(body) resolves with the response body. Resolves { result } when
// the batch was saved, { results } when the fallback saved it; rejects with
// conflictError on a version conflict, batchError otherwise.
export async function executeBatch(batch, { request, fallback }) {
  let response;

  try {
    response = await request(buildBatchPayload(batch));
  } catch (error) {
    const status = error?.response?.status;

    if (status === 404)
      return { results: await runBatchFallback(batch, fallback) };
    if (status === 409) throw conflictError(error.response.data);

    throw batchError(
      invalidConfigText(error?.response?.data) ??
        error?.response?.data?.error ??
        error?.message ??
        error
    );
  }

  if (response?.status === "OK") return { result: response };
  if (response?.status === "CONFLICT") throw conflictError(response);

  throw batchError(
    invalidConfigText(response) ??
      response?.error ??
      `Save ${response?.status ?? "failed"}`
  );
}

// Notifications: one per item, as when each item had its own call. An item
// with notify: false (UI only, not sent) is left out, as the calls that
// notified only on error.
export function batchSuccessMessages({
  items = [],
  deletes = [],
  modifiche
} = {}) {
  const messages = [
    ...items
      .filter((item) => item.notify !== false)
      .map(({ accessor, Id }) => `${accessor} Id: ${Id} Saved!`),
    ...deletes
      .filter((del) => del.notify !== false)
      .map(({ accessor, Id, id }) => `${accessor} Id: ${Id ?? id} Deleted!`)
  ];

  if (messages.length === 0 && items.length === 0 && hasModifiche(modifiche))
    messages.push("Modifiche Saved!");

  return messages;
}

export function batchConfirmMessage({ items = [], deletes = [] } = {}) {
  const what = [
    ...items.map(({ accessor, Id }) => `${accessor} Id: ${Id}`),
    ...deletes.map(
      ({ accessor, Id, id }) => `delete ${accessor} Id: ${Id ?? id}`
    )
  ];

  return `Are you Really sure you want to Save ${what.join(" and ") || "Modifiche"}?`;
}

// A conflict of a 409 (or refused locally): a row saved or deleted by
// someone else meanwhile (reload it), or an id a colleague created meanwhile
// that a rename or a new item targets (reason "exists": another id).
export function batchErrorMessage(error, data = {}) {
  if (data.type === "conflict") {
    const conflicts = data.conflicts ?? [];
    const describe = ({ type, id, lastSaved, action, reason }) => {
      const by = lastSaved?.username ? ` by ${lastSaved.username}` : "";
      const on = lastSaved?.date
        ? ` on ${new Date(lastSaved.date).toLocaleString()}`
        : "";
      if (reason === "exists")
        return `${type} Id: ${id} was created${by}${on}: choose another id`;
      const what = action === "delete" ? "deleted" : "saved";

      return `${type} Id: ${id} was ${what}${by}${on}`;
    };
    const exists = conflicts.filter(({ reason }) => reason === "exists");
    const stale = conflicts.filter(({ reason }) => reason !== "exists");

    const sentences = [];
    if (stale.length || !exists.length)
      sentences.push(
        `${stale.map(describe).join(" and ") || "an item was saved by someone else"} meanwhile: reload it before saving`
      );
    if (exists.length) sentences.push(exists.map(describe).join(" and "));

    return `Not saved, ${sentences.join("; ")}.`;
  }

  if (data.accessor) return `${data.accessor} Id: ${data.Id} ${error}!`;

  const text =
    typeof error === "string"
      ? error
      : (error?.message ?? JSON.stringify(error));

  return `Save Error: ${text}`;
}
